import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { initialState } from '../store/Auth/Auth.reducer';

/**
 * @typedef {Object} User
 * @property {number|null} user_id
 * @property {string|null} email
 * @property {string|null} role
 * @property {number|null} doctor_distr_group_id
 * @property {number|null} distr_group_id
 * @property {number|null} country_id
 * @property {number|null} admin_group_id
 */

/**
 * Represents the auth object with its properties.
 * @typedef {Object} AuthObject
 * @property {string} status - The status of authentication.
 * @property {User} user - The authenticated user object.
 * @property {string|null} error - The authentication error message.
 * @property {boolean} isAuthenticated - Indicates if the user is authenticated.
 * @property {string} isAuthenticatedText - The text indicating the authentication status.
 * @property {string} registeredStatus - The registration status.
 * @property {string} registeredStatusText - The text indicating the registration status.
 * @property {string} passwordUpdateSuccessText - The text indicating password update success.
 * @property {string} passwordUpdateFailureText - The text indicating password update failure.
 * @property {boolean} loggedOut - Indicates if the user has been logged out.
 */

/**
 * Custom React hook to retrieve the auth object from the Redux store.
 * @returns {AuthObject} The auth object with its properties.
 */
const useAuth = () => {
    const store = useSelector((store) => store);

    /**
     * The auth object with its properties.
     * @type {AuthObject}
     */
    const auth = useMemo(() => {
        return store && store?.auth ? store.auth : initialState;
    }, [store?.auth]);

    return auth;
};

export default useAuth;
